import { useEffect, useState } from "react";
import { Box, Button, Chip, FormControl, IconButton, MenuItem, Select, Typography, Paper } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { TeamId } from "../interface/GameTypes";
import { Connection, MatchPlayerNetPayload, ProposedPlayer, SetRecordPayload } from "../sockets-client";
import { AvatarGenerator } from "random-avatar-generator";
import { usePostHog } from "@posthog/react";
import { absoluteJoinUrl } from "../utils/joinPath";

const generator = new AvatarGenerator();
const BOT_GRADIENTS = [
  "linear-gradient(145deg, #8e24aa 0%, #5e35b1 55%, #3949ab 100%)",
  "linear-gradient(145deg, #ef5350 0%, #fb8c00 55%, #fdd835 100%)",
  "linear-gradient(145deg, #00897b 0%, #00acc1 52%, #1e88e5 100%)",
  "linear-gradient(145deg, #43a047 0%, #7cb342 55%, #c0ca33 100%)",
  "linear-gradient(145deg, #6d4c41 0%, #8d6e63 55%, #bcaaa4 100%)",
  "linear-gradient(145deg, #d81b60 0%, #8e24aa 55%, #5e35b1 100%)",
  "linear-gradient(145deg, #00acc1 0%, #26c6da 52%, #66bb6a 100%)",
  "linear-gradient(145deg, #f4511e 0%, #fb8c00 55%, #ffca28 100%)",
];

const botGradientForPlayer = (player: ProposedPlayer) => {
  const seedBase = `${player.uuid}:${player.seatIndex}:${player.name ?? ""}`;
  let hash = 0;
  for (let i = 0; i < seedBase.length; i += 1) {
    hash = (hash * 31 + seedBase.charCodeAt(i)) >>> 0;
  }
  return BOT_GRADIENTS[hash % BOT_GRADIENTS.length];
};

interface Props {
  proposedPlayers: ProposedPlayer[];
  myUUID: string;
  hostUUID: string;
  socketConnection: Connection;
  roomID: string;
  /** Rummy: everyone is effectively solo — show a single seat-order column instead of Team A/B. */
  seatingMode?: "teams" | "table";
  /** e.g. `/addata` — used for host-only table size / lobby bots. */
  namespacePath: string | null;
  /** Max seats for this room (Addata: 4 / 6 / 8 / 12). */
  roomMaxPlayers: number;
  /** Addata lobby: cumulative match totals (for warnings before shuffle / swap / resize). */
  matchTeamPoints?: { A: number; B: number };
  matchGamesPlayed?: number;
  /** Per-seat cumulative match net (same rule as in-game). */
  matchPlayerPoints?: MatchPlayerNetPayload[];
  setRecord?: SetRecordPayload;
}

export const TeamSetupScreen = ({
  proposedPlayers,
  myUUID,
  hostUUID,
  socketConnection,
  roomID,
  seatingMode = "teams",
  namespacePath,
  roomMaxPlayers,
  matchTeamPoints,
  matchGamesPlayed = 0,
  matchPlayerPoints,
  setRecord,
}: Props) => {
  const [selectedSetSize, setSelectedSetSize] = useState<3 | 5 | 7>(setRecord?.setSize ?? 3);
  const [literatureScoringMode, setLiteratureScoringMode] = useState<"standard" | "weighted">("standard");
  useEffect(() => {
    if (setRecord?.setSize) setSelectedSetSize(setRecord.setSize);
  }, [setRecord?.setSize]);
  const posthog = usePostHog();
  const [selectedUUID, setSelectedUUID] = useState<string | null>(null);
  const [copyInviteFeedback, setCopyInviteFeedback] = useState(false);
  const isHost = myUUID === hostUUID;
  const tableOrder = seatingMode === "table";
  const pts = matchTeamPoints ?? { A: 0, B: 0 };
  const hasAddataMatchProgress =
    namespacePath === "/addata" &&
    (matchGamesPlayed > 0 || pts.A !== 0 || pts.B !== 0);

  const confirmAddataMatchReset = (verb: string): boolean => {
    if (!hasAddataMatchProgress) return true;
    return window.confirm(
      `This table has match progress (Team A ${pts.A}, Team B ${pts.B}${matchGamesPlayed > 0 ? `, deals finished: ${matchGamesPlayed}` : ""}). ${verb} resets the match streak (scores and ranked row). Continue?`
    );
  };

  const teamA = tableOrder
    ? [...proposedPlayers].sort((a, b) => a.seatIndex - b.seatIndex)
    : proposedPlayers.filter((p) => p.teamId === TeamId.A);
  const teamB = tableOrder ? [] : proposedPlayers.filter((p) => p.teamId === TeamId.B);

  const handlePlayerClick = (uuid: string) => {
    if (!isHost) return;
    if (selectedUUID === null) {
      setSelectedUUID(uuid);
    } else if (selectedUUID === uuid) {
      setSelectedUUID(null);
    } else {
      if (!confirmAddataMatchReset("Swapping seats")) return;
      socketConnection.swapPlayers(selectedUUID, uuid);
      setSelectedUUID(null);
    }
  };

  const handleRandomize = () => {
    if (!isHost) return;
    if (!confirmAddataMatchReset("Shuffling teams")) return;
    setSelectedUUID(null);
    socketConnection.shuffleTeams();
  };

  const handleStartGame = () => {
    posthog?.capture("game_started", {
      room_id: roomID,
      player_count: proposedPlayers.length,
      bot_count: proposedPlayers.filter((p) => p.isBot).length,
      namespace: namespacePath,
      seating_mode: seatingMode,
    });
    socketConnection.confirmStart(
      namespacePath === "/addata" ? selectedSetSize : undefined,
      namespacePath === "/literature" ? literatureScoringMode : undefined
    );
  };

  const handleLeaveRoom = () => {
    posthog?.capture("room_left", { room_id: roomID, namespace: namespacePath, is_host: isHost });
    socketConnection.leaveRoom();
  };

  const handleHostRemovePlayer = (uuid: string, label: string) => {
    if (!isHost || namespacePath !== "/addata") return;
    if (uuid === myUUID) return;
    if (!window.confirm(`Remove ${label} from the room?`)) return;
    posthog?.capture("host_remove_player", { room_id: roomID, target_uuid: uuid });
    socketConnection.hostKickPlayer(uuid);
  };

  const showAddataLobbyControls = isHost && namespacePath === "/addata" && seatingMode === "teams";
  const showLiteratureLobbyControls = isHost && namespacePath === "/literature" && seatingMode === "teams";
  const isRoomFull = proposedPlayers.length === roomMaxPlayers;
  const seatsRemaining = Math.max(0, roomMaxPlayers - proposedPlayers.length);
  /** Count blinds for +/- UI even if `isBot` was missing on a stale payload (name still `Blind …`). */
  const lobbyBotCount = proposedPlayers.filter(
    (p) => p.isBot === true || /^Blind /i.test(p.name ?? "")
  ).length;

  const handleTableSize = (n: 4 | 6 | 8 | 12) => {
    if (!isHost || namespacePath !== "/addata") return;
    if (n === roomMaxPlayers) return;
    if (!confirmAddataMatchReset("Changing table size")) return;
    socketConnection.updateRoomCapacity(n);
  };

  const handleAddOneLobbyBot = () => {
    if (!isHost || seatsRemaining <= 0) return;
    socketConnection.addBotsLobby(1);
  };

  const handleRemoveOneLobbyBot = () => {
    if (!isHost || lobbyBotCount <= 0) return;
    socketConnection.removeBotsLobby(1);
  };

  const inviteUrl =
    namespacePath && roomID.length > 0 ? absoluteJoinUrl(namespacePath, roomID) : "";

  const copyInviteLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopyInviteFeedback(true);
      posthog?.capture("invite_link_copied", { room_id: roomID, namespace: namespacePath, source: "team_setup" });
      setTimeout(() => setCopyInviteFeedback(false), 2000);
    } catch {
      /* clipboard may be unavailable */
    }
  };

  const renderPlayer = (player: ProposedPlayer, teamColor: string, showSeatPrefix?: boolean) => {
    const isSelected = selectedUUID === player.uuid;
    const isMe = player.uuid === myUUID;
    const isPlayerHost = player.uuid === hostUUID;
    const avatarUrl = player.isBot ? null : generator.generateRandomAvatar(player.uuid);
    const botGradient = player.isBot ? botGradientForPlayer(player) : undefined;
    const showHostRemove = isHost && namespacePath === "/addata" && !player.isBot && !isMe;

    return (
      <Box
        key={player.uuid}
        onClick={() => handlePlayerClick(player.uuid)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          p: { xs: 0.75, sm: 1.25 },
          borderRadius: 2,
          cursor: isHost ? "pointer" : "default",
          border: isSelected
            ? "2px solid rgba(255,255,255,0.55)"
            : player.isBot
            ? "2px solid rgba(255,255,255,0.2)"
            : "2px solid rgba(255,255,255,0.18)",
          backgroundColor: isSelected
            ? "rgba(255,215,0,0.15)"
            : player.isBot
            ? "rgba(255,255,255,0.03)"
            : "rgba(255,255,255,0.05)",
          transition: "all 0.15s",
          "&:hover": isHost
            ? {
                backgroundColor: isSelected
                  ? "rgba(255,215,0,0.2)"
                  : "rgba(255,255,255,0.1)",
              }
            : {},
        }}
      >
        {player.isBot ? (
          <Box
            sx={{
              width: { xs: 30, sm: 40 },
              height: { xs: 30, sm: 40 },
              borderRadius: "50%",
              border: "2px solid rgba(255,255,255,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: botGradient,
              boxShadow: "0 0 0 2px rgba(255,255,255,0.08)",
              flexShrink: 0,
            }}
          >
            <SmartToyIcon sx={{ color: "#fff", fontSize: { xs: 16, sm: 22 } }} />
          </Box>
        ) : (
          <Box
            component="img"
            src={avatarUrl!}
            alt={player.name ?? "player"}
            sx={{
              width: { xs: 30, sm: 40 },
              height: { xs: 30, sm: 40 },
              borderRadius: "50%",
              border: "2px solid rgba(255,255,255,0.35)",
              flexShrink: 0,
            }}
          />
        )}
        <Typography
          component="div"
          color={player.isBot ? "rgba(255,255,255,0.5)" : "white"}
          sx={{
            fontWeight: isMe ? "bold" : "normal",
            flex: 1,
            minWidth: 0,
            fontStyle: player.isBot ? "italic" : "normal",
            fontSize: { xs: "0.8rem", sm: "1rem" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {showSeatPrefix ? `${player.seatIndex + 1}. ` : ""}
          {player.name ?? `Seat ${player.seatIndex}`}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            flexShrink: 0,
            flexWrap: "wrap",
            maxWidth: "100%",
          }}
        >
          {player.isBot && (
            <Chip
              label="Blind"
              size="small"
              sx={{ height: 18, fontSize: "0.65rem", backgroundColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" }}
            />
          )}
          {isMe && !player.isBot && (
            <Chip label="You" size="small" sx={{ height: 18, fontSize: "0.65rem", backgroundColor: "rgba(255,255,255,0.82)", color: "#0e1116" }} />
          )}
          {isPlayerHost && !player.isBot && (
            <Chip label="Host" size="small" sx={{ height: 18, fontSize: "0.65rem", backgroundColor: "#1976d2", color: "#fff" }} />
          )}
          {isSelected && (
            <Chip
              label="Selected"
              size="small"
              sx={{ backgroundColor: "#FFD700", color: "#000", fontWeight: "bold", fontSize: "0.65rem" }}
            />
          )}
          {showHostRemove && (
            <Button
              size="small"
              variant="text"
              color="error"
              sx={{ minWidth: 0, px: 0.5, fontSize: "0.65rem", textTransform: "none" }}
              onClick={(e) => {
                e.stopPropagation();
                handleHostRemovePlayer(player.uuid, player.name ?? `Seat ${player.seatIndex}`);
              }}
            >
              Remove
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const teamSection = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        flexWrap: "nowrap",
        gap: { xs: 1, sm: 1.5 },
        justifyContent: "center",
        alignItems: "stretch",
        width: "100%",
        maxWidth: 1080,
        minWidth: 0,
        mx: "auto",
      }}
    >
      <Paper
        elevation={4}
        sx={{
          backgroundColor: "rgba(0,0,0,0.7)",
          border: "1px solid rgba(255,255,255,0.16)",
          borderRadius: 2,
          padding: { xs: 1.5, sm: 2 },
          flex: "1 1 0",
          minWidth: 0,
          maxWidth: "none",
          minHeight: 188,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: { xs: 0.5, sm: 1.2 } }}>
          <Typography variant="subtitle1" sx={{ color: "rgba(255,255,255,0.92)", fontWeight: "bold", fontSize: { xs: "0.95rem", sm: "1rem" } }}>
            {tableOrder ? "Seats (deal order)" : "Team A"}
          </Typography>
          {!tableOrder ? (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 600 }}>
              {teamA.length} players
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {teamA.map((p) => renderPlayer(p, "rgba(255,255,255,0.35)", tableOrder))}
        </Box>
      </Paper>

      {!tableOrder && (
        <Paper
          elevation={4}
          sx={{
            backgroundColor: "rgba(0,0,0,0.7)",
            border: "1px solid rgba(255,255,255,0.16)",
            borderRadius: 2,
            padding: { xs: 1.5, sm: 2 },
            flex: "1 1 0",
            minWidth: 0,
            maxWidth: "none",
            minHeight: 188,
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: { xs: 0.5, sm: 1.2 } }}>
            <Typography variant="subtitle1" sx={{ color: "rgba(255,255,255,0.92)", fontWeight: "bold", fontSize: { xs: "0.95rem", sm: "1rem" } }}>
              Team B
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 600 }}>
              {teamB.length} players
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {teamB.map((p) => renderPlayer(p, "rgba(255,255,255,0.35)"))}
          </Box>
        </Paper>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 0.75, sm: 1.2, md: 1.6 },
        padding: { xs: 1, sm: 1.25, md: 1.5 },
        paddingBottom: "max(16px, env(safe-area-inset-bottom, 0px))",
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: "100vh",
        justifyContent: "flex-start",
        overflowX: "hidden",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1080, px: { xs: 0.5, sm: 0 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.2, mb: 1.2 }}>
          <Box sx={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: 1 }}>
            <IconButton
              size="small"
              onClick={handleLeaveRoom}
              aria-label="Back to room selection"
              sx={{ color: "rgba(255,255,255,0.85)", mt: 0.15 }}
            >
              <ArrowBackRoundedIcon fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" color="white" fontWeight="bold" sx={{ fontSize: { xs: "1.45rem", sm: "2rem" }, lineHeight: 1.1 }}>
              {tableOrder ? "Rummy Lobby" : "Addata Lobby"}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.66)", fontSize: "0.86rem" }}>
              Room: {roomID}
            </Typography>
            </Box>
          </Box>
          {inviteUrl ? (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<ContentCopyIcon />}
              onClick={copyInviteLink}
              sx={{
                color: "rgba(255,255,255,0.9)",
                borderColor: "rgba(255,255,255,0.24)",
                minHeight: 34,
                whiteSpace: "nowrap",
                "&:hover": { borderColor: "rgba(255,255,255,0.45)" },
              }}
            >
              {copyInviteFeedback ? "Copied" : "Copy Invite"}
            </Button>
          ) : null}
        </Box>
      </Box>
      {showAddataLobbyControls ? (
        <Box sx={{ width: "100%", maxWidth: 1080, mx: "auto" }}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 1, sm: 0.8 },
              mb: 1.2,
              backgroundColor: "rgba(30,30,34,0.9)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 2,
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "auto auto minmax(120px, 1fr) minmax(120px, 1fr)" },
              alignItems: "center",
              gap: { xs: 1, sm: 0.7 },
              "@media (orientation: landscape) and (min-width: 700px)": {
                p: 0.65,
                gap: 0.55,
              },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleRandomize}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.2)",
                minHeight: { xs: 34, sm: 30 },
                width: "100%",
                fontSize: { xs: "0.82rem", sm: "0.75rem" },
              }}
            >
                Randomize
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, width: "100%" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddOneLobbyBot}
                disabled={seatsRemaining === 0}
                startIcon={<AddIcon fontSize="small" />}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.2)",
                  minHeight: { xs: 34, sm: 30 },
                  fontSize: { xs: "0.82rem", sm: "0.75rem" },
                  flex: 1,
                }}
              >
                Add Bot
              </Button>
              {lobbyBotCount > 0 ? (
                <IconButton
                  size="small"
                  aria-label="Remove one blind bot"
                  onClick={handleRemoveOneLobbyBot}
                  sx={{
                    color: "rgba(255,255,255,0.82)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    p: { xs: 0.7, sm: 0.55 },
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
            <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 96 }, width: "100%" }}>
                <Select
                  value={String(roomMaxPlayers)}
                  onChange={(e) => handleTableSize(Number(e.target.value) as 4 | 6 | 8 | 12)}
                  sx={{
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.06)",
                    minHeight: { xs: 38, sm: 32 },
                    fontSize: { xs: "0.82rem", sm: "0.75rem" },
                    ".MuiSelect-select": { py: { xs: 0.8, sm: 0.55 } },
                    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
                  }}
                >
                  {[4, 6, 8, 12].map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n} Players
                    </MenuItem>
                  ))}
                </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 96 }, width: "100%" }}>
                <Select
                  value={String(selectedSetSize)}
                  onChange={(e) => setSelectedSetSize(Number(e.target.value) as 3 | 5 | 7)}
                  disabled={(setRecord?.matchesPlayed ?? 0) > 0 && !(setRecord?.setComplete ?? false)}
                  sx={{
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.06)",
                    minHeight: { xs: 38, sm: 32 },
                    fontSize: { xs: "0.82rem", sm: "0.75rem" },
                    ".MuiSelect-select": { py: { xs: 0.8, sm: 0.55 } },
                    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
                  }}
                >
                  {[3, 5, 7].map((n) => (
                    <MenuItem key={n} value={String(n)}>
                      Match of {n}
                    </MenuItem>
                  ))}
                </Select>
            </FormControl>
          </Paper>
          {teamSection}
        </Box>
      ) : (
        teamSection
      )}
      {showLiteratureLobbyControls ? (
        <Box sx={{ width: "100%", maxWidth: 1080, mx: "auto", mt: 1 }}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 1, sm: 0.8 },
              backgroundColor: "rgba(30,30,34,0.9)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.78)", minWidth: 130 }}>
              Scoring mode
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={literatureScoringMode}
                onChange={(e) => setLiteratureScoringMode(e.target.value as "standard" | "weighted")}
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.06)",
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
                }}
              >
                <MenuItem value="standard">Standard (all sets 1 point)</MenuItem>
                <MenuItem value="weighted">Weighted (major 2, minor 1)</MenuItem>
              </Select>
            </FormControl>
          </Paper>
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 2 },
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          mt: { xs: 0.5, sm: 1 },
        }}
      >
        {isHost ? (
          <>
            <Button
              variant="contained"
              color="success"
              size="medium"
              onClick={handleStartGame}
              disabled={!isRoomFull}
              startIcon={<PlayArrowRoundedIcon fontSize="small" />}
              sx={{ minWidth: { xs: 120, sm: 160 }, minHeight: 44, fontWeight: "bold", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              Start Game
            </Button>
            {!isRoomFull ? (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", width: "100%", textAlign: "center" }}>
                Waiting for more players to join... ({proposedPlayers.length}/{roomMaxPlayers} seats)
              </Typography>
            ) : null}
          </>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.5)", fontStyle: "italic", alignSelf: "center" }}
          >
            Waiting for the host to start the game…
          </Typography>
        )}
        <Button
          variant="contained"
          color="error"
          size="medium"
          onClick={handleLeaveRoom}
          startIcon={<LogoutRoundedIcon fontSize="small" />}
          sx={{
            minWidth: { xs: 120, sm: 160 },
            minHeight: 44,
            fontWeight: "bold",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          }}
        >
          Leave room
        </Button>
      </Box>
    </Box>
  );
};
